import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  LinearProgress,
  Alert,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from 'recharts';
import { api } from '../services/api';
import { usePermissions } from '../hooks/usePermissions';
import { useSearchParams } from 'react-router-dom';

// Données mockées pour les visualisations
const mockSurveyData = {
  totalResponses: 247,
  targetResponses: 300,
  completionRate: 82.3,
  responsesByZone: [
    { zone: 'Dantokpa', responses: 89, target: 100, color: '#1976d2' },
    { zone: 'Ganhi', responses: 78, target: 100, color: '#2e7d32' },
    { zone: 'Fidjrossè', responses: 80, target: 100, color: '#ed6c02' },
  ],
  responsesByDay: [
    { date: '14/11', responses: 45 },
    { date: '13/11', responses: 52 },
    { date: '12/11', responses: 38 },
    { date: '11/11', responses: 41 },
    { date: '10/11', responses: 35 },
    { date: '09/11', responses: 36 },
  ],
  ageDistribution: [
    { age: '18-25', count: 45, percentage: 18.2 },
    { age: '26-35', count: 89, percentage: 36.0 },
    { age: '36-45', count: 78, percentage: 31.6 },
    { age: '46-55', count: 28, percentage: 11.3 },
    { age: '55+', count: 7, percentage: 2.8 },
  ],
  frequencyDistribution: [
    { frequency: 'Quotidien', count: 89, color: '#1976d2' },
    { frequency: 'Hebdomadaire', count: 112, color: '#2e7d32' },
    { frequency: 'Mensuel', count: 46, color: '#ed6c02' },
  ],
  brandPreference: [
    { brand: 'SOBEBRA', count: 156, percentage: 63.2 },
    { brand: 'Autre', count: 91, percentage: 36.8 },
  ],
  geographicData: [
    { id: 1, lat: 6.3654, lng: 2.4183, zone: 'Dantokpa', responses: 89, intensity: 'high' },
    { id: 2, lat: 6.3600, lng: 2.4100, zone: 'Ganhi', responses: 78, intensity: 'medium' },
    { id: 3, lat: 6.3700, lng: 2.4200, zone: 'Fidjrossè', responses: 80, intensity: 'high' },
  ],
  performanceComparison: [
    { metric: 'Taux de réponse', agent1: 85, agent2: 92, agent3: 78 },
    { metric: 'Qualité données', agent1: 88, agent2: 90, agent3: 85 },
    { metric: 'Ponctualité', agent1: 92, agent2: 95, agent3: 88 },
    { metric: 'Couverture zone', agent1: 90, agent2: 88, agent3: 85 },
  ],
};

const COLORS = ['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#f44336'];

const SurveyAnalytics: React.FC = () => {
  const { hasPermission } = usePermissions();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId');
  const surveyId = searchParams.get('surveyId');
  const formId = searchParams.get('formId');
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('7days');
  const [selectedZone, setSelectedZone] = useState('all');
  const [selectedSurvey, setSelectedSurvey] = useState('all');

  useEffect(() => {
    const loadAnalytics = async () => {
      try {
        setLoading(true);
        // Charger les données réelles depuis Supabase
        if (formId) {
          const { submissions } = await api.forms.getSubmissions();
          // Traiter les données pour les analytics
          // Pour l'instant, on garde les données mockées mais on pourrait les calculer depuis les submissions
        }
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement des analytics:', error);
        setLoading(false);
      }
    };
    
    loadAnalytics();
    // Si un projectId est fourni, filtrer les données par projet
    if (projectId) {
      setSelectedSurvey(formId || 'all');
    }
  }, [projectId, formId]);

  if (!hasPermission('surveys:view_analytics') && !hasPermission('surveys:view_readonly')) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" color="error">
          Accès non autorisé. Vous n'avez pas les permissions nécessaires pour consulter les analytics.
        </Typography>
      </Box>
    );
  }

  // Filtrer les données si un projectId est fourni
  const filteredData = projectId ? {
    ...mockSurveyData,
    // Filtrer les réponses par projet (simulation - dans une vraie app, cela viendrait de l'API)
    totalResponses: Math.floor(mockSurveyData.totalResponses * 0.6),
    completionRate: Math.floor(mockSurveyData.completionRate * 0.9),
  } : mockSurveyData;

  const completionPercentage = (filteredData.completionRate / 100) * 100;
  const getCompletionColor = (percentage: number) => {
    if (percentage >= 80) return '#2e7d32';
    if (percentage >= 60) return '#ed6c02';
    return '#f44336';
  };

  // Composant de jauge circulaire
  const CircularGauge: React.FC<{ value: number; max: number; label: string; color?: string }> = ({
    value,
    max,
    label,
    color = '#1976d2',
  }) => {
    const percentage = Math.min((value / max) * 100, 100);
    const circumference = 2 * Math.PI * 70;
    const offset = circumference - (percentage / 100) * circumference;

    return (
      <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress
          variant="determinate"
          value={percentage}
          size={120}
          thickness={6}
          sx={{
            color: getCompletionColor(percentage),
            transform: 'rotate(-90deg)',
          }}
        />
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
          }}
        >
          <Typography variant="h5" component="div" fontWeight="bold">
            {value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
        </Box>
      </Box>
    );
  };

  // Composant de carte simplifiée (sans dépendance externe)
  const SimpleMap: React.FC = () => {
    return (
      <Box
        sx={{
          width: '100%',
          height: 400,
          bgcolor: '#e3f2fd',
          borderRadius: 2,
          position: 'relative',
          overflow: 'hidden',
          border: '2px solid #1976d2',
        }}
      >
        {/* Fond de carte stylisé */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
            opacity: 0.5,
          }}
        />
        
        {/* Points de collecte */}
        {filteredData.geographicData.map((point) => {
          const size = point.intensity === 'high' ? 20 : point.intensity === 'medium' ? 15 : 10;
          const left = 20 + (point.lng - 2.41) * 5000; // Approximation pour positionnement
          const top = 50 + (point.lat - 6.36) * 5000;
          
          return (
            <Box
              key={point.id}
              sx={{
                position: 'absolute',
                left: `${Math.max(10, Math.min(90, left))}%`,
                top: `${Math.max(10, Math.min(90, top))}%`,
                width: size,
                height: size,
                borderRadius: '50%',
                bgcolor: point.intensity === 'high' ? '#f44336' : point.intensity === 'medium' ? '#ed6c02' : '#ffc107',
                border: '3px solid white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'scale(1.3)',
                  zIndex: 10,
                },
                transition: 'all 0.3s',
              }}
              title={`${point.zone}: ${point.responses} réponses`}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: size + 5,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  bgcolor: 'rgba(0,0,0,0.8)',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  whiteSpace: 'nowrap',
                  opacity: 0,
                  transition: 'opacity 0.3s',
                  '&:hover': {
                    opacity: 1,
                  },
                }}
              >
                {point.zone}: {point.responses}
              </Box>
            </Box>
          );
        })}
        
        {/* Légende */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 16,
            right: 16,
            bgcolor: 'white',
            padding: 2,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
        >
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Densité de réponses
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#f44336' }} />
            <Typography variant="caption">Élevée (&gt;80)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ed6c02' }} />
            <Typography variant="caption">Moyenne (50-80)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ffc107' }} />
            <Typography variant="caption">Faible (&lt;50)</Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* En-tête avec filtres */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Suivi des Sondages
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Analyse des données de consommation et de terrain - Cotonou, Bénin
        </Typography>
        {projectId && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Filtrage par projet activé</strong> - Les données affichées sont spécifiques au projet sélectionné.
            </Typography>
          </Alert>
        )}

        {/* Filtres intelligents */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Période</InputLabel>
                <Select value={selectedPeriod} onChange={(e) => setSelectedPeriod(e.target.value)}>
                  <MenuItem value="7days">7 derniers jours</MenuItem>
                  <MenuItem value="30days">30 derniers jours</MenuItem>
                  <MenuItem value="90days">3 derniers mois</MenuItem>
                  <MenuItem value="all">Tout</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Zone géographique</InputLabel>
                <Select value={selectedZone} onChange={(e) => setSelectedZone(e.target.value)}>
                  <MenuItem value="all">Toutes les zones</MenuItem>
                  <MenuItem value="dantokpa">Dantokpa</MenuItem>
                  <MenuItem value="ganhi">Ganhi</MenuItem>
                  <MenuItem value="fidjrosse">Fidjrossè</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Sondage</InputLabel>
                <Select value={selectedSurvey} onChange={(e) => setSelectedSurvey(e.target.value)}>
                  <MenuItem value="all">Tous les sondages</MenuItem>
                  <MenuItem value="consumer">Sondage Consommateurs</MenuItem>
                  <MenuItem value="activation">Rapport d'Activation PDV</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Cartes de synthèse KPIs */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', bgcolor: '#f5f5f5' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Total Réponses
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {filteredData.totalResponses}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Cible: {filteredData.targetResponses}
                  </Typography>
                </Box>
                <PeopleIcon sx={{ fontSize: 48, color: '#1976d2', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', bgcolor: '#f5f5f5' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Taux de Complétion
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color={getCompletionColor(filteredData.completionRate)}>
                    {filteredData.completionRate}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={filteredData.completionRate}
                    sx={{
                      mt: 1,
                      height: 8,
                      borderRadius: 4,
                      bgcolor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: getCompletionColor(filteredData.completionRate),
                      },
                    }}
                  />
                </Box>
                <CheckCircleIcon sx={{ fontSize: 48, color: getCompletionColor(filteredData.completionRate), opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', bgcolor: '#f5f5f5' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Zones Couvertes
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {filteredData.responsesByZone.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Sur {filteredData.responsesByZone.length} zones
                  </Typography>
                </Box>
                <LocationIcon sx={{ fontSize: 48, color: '#2e7d32', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', bgcolor: '#f5f5f5' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Tendance
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="#2e7d32">
                    +12%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    vs semaine dernière
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 48, color: '#2e7d32', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Carte géographique interactive */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Carte de Densité des Réponses
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Visualisation géographique des points de collecte dans la région de Cotonou
            </Typography>
            <SimpleMap />
          </Paper>
        </Grid>
      </Grid>

      {/* Jauges de complétion par zone */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Complétion par Zone Géographique
          </Typography>
        </Grid>
        {filteredData.responsesByZone.map((zone) => {
          const percentage = (zone.responses / zone.target) * 100;
          return (
            <Grid item xs={12} sm={4} key={zone.zone}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <CircularGauge
                      value={zone.responses}
                      max={zone.target}
                      label={zone.zone}
                      color={zone.color}
                    />
                    <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
                      {zone.responses} / {zone.target} réponses
                    </Typography>
                    <Chip
                      label={`${percentage.toFixed(1)}%`}
                      size="small"
                      sx={{
                        mt: 1,
                        bgcolor: getCompletionColor(percentage),
                        color: 'white',
                        fontWeight: 'bold',
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Graphiques comparatifs */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Distribution par âge */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Distribution par Tranche d'Âge
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={filteredData.ageDistribution}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="age" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#1976d2" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Fréquence d'achat */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Fréquence d'Achat
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={filteredData.frequencyDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry: any) => {
                    const total = filteredData.frequencyDistribution.reduce((sum, item) => sum + item.count, 0);
                    const pct = ((entry.count / total) * 100).toFixed(1);
                    return `${entry.frequency}: ${pct}%`;
                  }}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {filteredData.frequencyDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Tendance des réponses */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Évolution des Réponses (7 derniers jours)
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={filteredData.responsesByDay}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="responses"
                  stroke="#1976d2"
                  strokeWidth={3}
                  dot={{ fill: '#1976d2', r: 6 }}
                  activeDot={{ r: 8 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Préférence de marque */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Préférence de Marque
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={filteredData.brandPreference} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="brand" type="category" />
                <Tooltip />
                <Bar dataKey="count" fill="#2e7d32" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Comparaison de performance des agents */}
      {hasPermission('surveys:view_analytics') && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Comparaison de Performance des Agents
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Analyse comparative des performances sur différents indicateurs clés
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <RadarChart data={filteredData.performanceComparison}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="metric" />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} />
                  <Radar
                    name="Koffi Amoussou"
                    dataKey="agent1"
                    stroke="#1976d2"
                    fill="#1976d2"
                    fillOpacity={0.6}
                  />
                  <Radar
                    name="Marie Adékambi"
                    dataKey="agent2"
                    stroke="#2e7d32"
                    fill="#2e7d32"
                    fillOpacity={0.6}
                  />
                  <Radar
                    name="Jean Sègbè"
                    dataKey="agent3"
                    stroke="#ed6c02"
                    fill="#ed6c02"
                    fillOpacity={0.6}
                  />
                  <Legend />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default SurveyAnalytics;

